<!-- Modal Overlay: hide checkout while payment success modal is shown; keep open on failed payment -->
<div
  *ngIf="
    isOpen &&
    !showPaymentSuccess &&
    !showPrepaidRedeemedSuccess &&
    !shouldShowVendorActiveSubscriptionModal()
  "
  class="modal-overlay"
>
  <!-- Modal Content -->
  <div
    class="modal-content"
    [class.modal-content--order-request]="
      showOrderRequestCheckoutLayout() || showFleetVehicleFoundServicesLayout()
    "
    [class.modal-content--checkout]="showPaymentCheckoutLayout()"
    [@slideInOut]
  >
    <!-- Header -->
    <div class="modal-header">
      <div class="header-text">
        <h3 class="modal-title">{{ getNotificationTitle() }}</h3>

        <!-- <p class="notification-message">{{ getStatusMessage() }}</p> -->
        <!-- <p class="notification-time">{{ getFormattedTime() }}</p> -->
      </div>
      <button
        type="button"
        class="close-button"
        (click)="onOrderRequestCloseClick()"
        aria-label="Close notification"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="modal-body">
      <!-- Subscription / greeting message (order-request flows only — not payment checkout) -->
      <div *ngIf="showSubscriptionSuggestionBanner" class="subscription-suggestion-message">
        <div class="suggestion-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L2 7L12 12L22 7L12 2Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 17L12 22L22 17"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 12L12 17L22 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="suggestion-content">
          <!-- CWS-1650 / CWS-1729: the getter drops a sentence the status strip replaces,
               and any upsell for a redeemable DRB member. -->
          <p *ngIf="subscriptionSuggestionSentence">
            {{ subscriptionSuggestionSentence }}
          </p>
          <!-- CWS-1729: a redeemable member's lookup and its answer sit in the Redeem row. -->
          <p
            *ngIf="vendorMembershipLookupResult && !vendorLookupInVerifyRow"
            class="suggestion-vendor-lookup__result"
            [class.is-success]="vendorMembershipLookupResult.severity === 'success'"
            [class.is-warn]="vendorMembershipLookupResult.severity === 'warn'"
            [class.is-error]="vendorMembershipLookupResult.severity === 'error'"
            role="status"
          >
            {{ vendorMembershipLookupResult.text }}
          </p>
        </div>
        <!-- CWS-1266: attendant-triggered live vendor (DRB) lookup on a refused card, right
             of the sentence that asks for it. The gate no longer asks the vendor; this does. -->
        <div
          *ngIf="showVendorMembershipLookup && !vendorLookupInVerifyRow"
          class="suggestion-vendor-lookup"
        >
          <button
            type="button"
            class="suggestion-vendor-lookup__button"
            data-testid="vendor-lookup-action"
            [disabled]="isVendorMembershipLookupRunning"
            [attr.aria-busy]="isVendorMembershipLookupRunning"
            (click)="onLookupVendorMembership()"
          >
            <span
              *ngIf="isVendorMembershipLookupRunning"
              class="suggestion-vendor-lookup__spinner"
              aria-hidden="true"
            ></span>
            <svg
              *ngIf="!isVendorMembershipLookupRunning"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M20 12a8 8 0 01-13.66 5.66M4 12a8 8 0 0113.66-5.66"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
              <path
                d="M17.5 3.5v3.2h-3.2M6.5 20.5v-3.2h3.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ vendorMembershipLookupButtonLabel }}
          </button>
        </div>
      </div>

      <!--
        CWS-1944 fix round 1 (item 1): the membership-refresh-failed notice is INDEPENDENT of the
        blocked gate below it — Task 8's retry-only-the-refresh path can set this flag on an
        already-`resolved_attendant` (never-blocked) entry, so it must render on its own condition,
        not nested inside `*ngIf="ocrDiscrepancy.blocked"` where it could never be seen.
      -->
      <p
        *ngIf="ocrMembershipRefreshFailed"
        class="ocr-discrepancy-block__refresh-failed ocr-discrepancy-block__refresh-failed--standalone"
        data-testid="ocr-membership-refresh-failed"
      >
        Membership info could not be refreshed.
      </p>

      <!-- CWS-1944 auto-fill: the plate was auto-corrected by the second-opinion OCR. Informational,
           never blocking — the corrected plate is already in the form and the attendant can edit it. -->
      <p
        *ngIf="ocrDiscrepancy.autoCorrection as auto"
        class="ocr-discrepancy-block__auto-corrected"
        data-testid="ocr-auto-corrected"
      >
        Plate auto-corrected from the camera read {{ auto.from }} to {{ auto.to }}. Please confirm
        it matches the vehicle.
      </p>

      <!-- CWS-1944: OCR plate verification discrepancy — hard-blocks proceeding until confirmed. -->
      <div
        *ngIf="ocrDiscrepancy.blocked"
        class="ocr-discrepancy-block"
        data-testid="ocr-discrepancy-block"
      >
        <p class="ocr-discrepancy-block__message">
          {{ ocrDiscrepancyMessageFor(ocrDiscrepancy) }}
        </p>
        <p *ngIf="ocrDiscrepancy.hint as hint" class="ocr-discrepancy-block__hint">
          {{ ocrMembershipHintTextFor(hint) }}
        </p>
        <div class="ocr-discrepancy-block__options">
          <button
            *ngFor="let option of ocrDiscrepancy.options"
            type="button"
            class="ocr-discrepancy-block__option"
            [attr.data-testid]="'ocr-option-' + option.kind"
            [disabled]="isConfirmingOcrPlate"
            [hidden]="option.kind === 'manual'"
            (click)="
              confirmOcrPlateChoice(ocrChoiceForOption(option.kind), option.plate, option.state)
            "
          >
            {{ option.plate }}<span *ngIf="option.state"> ({{ option.state }})</span>
          </button>
        </div>
        <div class="ocr-discrepancy-block__manual">
          <input
            type="text"
            placeholder="Plate"
            data-testid="ocr-manual-plate-input"
            [(ngModel)]="ocrManualPlateDraft"
            [disabled]="isConfirmingOcrPlate"
          />
          <input
            type="text"
            placeholder="State"
            data-testid="ocr-manual-state-input"
            [(ngModel)]="ocrManualStateDraft"
            [disabled]="isConfirmingOcrPlate"
          />
          <button
            type="button"
            data-testid="ocr-manual-confirm"
            [disabled]="isConfirmingOcrPlate"
            (click)="confirmOcrPlateChoice('manual', ocrManualPlateDraft, ocrManualStateDraft)"
          >
            Confirm plate
          </button>
        </div>
      </div>

      <!-- Order request: Figma-style layout (create + checkout edit order).
           Paint key remounts once async history/prepaid settle — first live SSE open
           otherwise keeps the initial loading snapshot (close/reopen works). -->
      <div
        class="order-request-layout"
        *ngIf="showOrderRequestCheckoutLayout()"
        [style.display]="isFamilyCheckout ? 'none' : null"
      >
        <app-notification-order-request-layout *ngFor="let _ of [orderRequestLayoutPaintKey]" />
      </div>

      <app-notification-payment-checkout-layout
        *ngIf="showPaymentCheckoutLayout() || isFamilyCheckout"
      />
      <div class="" *ngIf="notification?.type === 'detailing-appointment-found'">
        <div class="col-12-row">
          <div class="form-group">
            <label for="appointment-time">Appointment Time</label>
            <p class="appointment-p">{{ getFormattedAppointmentTime() }}</p>
          </div>
          <div class="form-group">
            <label for="appointment-time">Number plate</label>
            <p class="appointment-p">{{ notification?.data.plate }}</p>
          </div>
          <div class="form-group">
            <label for="appointment-time">Customer name</label>
            <p class="appointment-p">
              {{ notification?.data.customer_first_name }}
              {{ notification?.data.customer_last_name }}
            </p>
          </div>
          <div class="form-group">
            <label for="appointment-time">Customer email</label>
            <p class="appointment-p">{{ notification?.data.customer_email }}</p>
          </div>
        </div>
      </div>
      <div class="" *ngIf="notification?.type === 'order-success'">
        <div class="col-12-row">
          <div class="form-group">
            <label for="appointment-time">Customer Name</label>
            <p class="appointment-p">{{ getOrderSuccessCustomerNameDisplay() }}</p>
          </div>
          <div class="form-group">
            <label for="appointment-time">Customer Email</label>
            <p class="appointment-p">{{ getOrderSuccessCustomerEmailDisplay() }}</p>
          </div>
          <div class="form-group">
            <label for="appointment-time">Package Name</label>
            <p class="appointment-p">{{ notification?.data.package_name }}</p>
          </div>
          <div class="form-group">
            <label for="appointment-time">License Plate</label>
            <p class="appointment-p">{{ getOrderSuccessLicensePlateOrEmpty() }}</p>
          </div>
          <div class="form-group" *ngIf="!isWayOrderSuccessRedemption()">
            <label for="appointment-time">Package Price</label>
            <p class="appointment-p">{{ notification?.data.final_price }}</p>
          </div>
        </div>
      </div>
      <app-notification-fleet-vehicle-found-layout
        *ngIf="notification?.type === 'fleet-vehicle-found'"
      />
      <div class="" *ngIf="notification?.type === 'fleet-invoice-added'">
        <div class="col-12-row">
          <div class="form-group">
            <label for="license-plate">License Plate</label>
            <p class="appointment-p">
              {{
                notification?.data?.data?.license_plate ||
                  notification?.data?.license_plate ||
                  notification?.data?.plate
              }}
            </p>
          </div>
          <div class="form-group">
            <label for="fleet-name">Fleet Name</label>
            <p class="appointment-p">
              {{ notification?.data?.data?.fleet_name || notification?.data?.fleet_name }}
            </p>
          </div>
          <div class="form-group">
            <label for="package-name">Package Name</label>
            <p class="appointment-p">
              {{ notification?.data?.data?.package_name || notification?.data?.package_name }}
            </p>
          </div>
        </div>
      </div>
      <div class="" *ngIf="notification?.type === 'order-completed'">
        <div class="col-12-row">
          <div class="form-group">
            <label for="order-id">Customer Name</label>
            <p class="appointment-p">
              {{ notification?.data.customer_first_name }}
              {{ notification?.data.customer_last_name }}
            </p>
          </div>
          <div class="form-group">
            <label for="order-status">Vehicle License Plate</label>
            <p class="appointment-p">{{ getOrderSuccessLicensePlateDisplay() }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Package Name</label>
            <p class="appointment-p">{{ notification?.data.package_name }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Status</label>
            <p class="appointment-p">Completed</p>
          </div>
        </div>
      </div>
      <div
        class=""
        *ngIf="
          notification?.type === 'exit-active-subscription' ||
          notification?.type === 'exit-detailing-appointment-found'
        "
      >
        <div class="col-12-row">
          <div class="form-group">
            <label for="order-id">Customer Name</label>
            <p class="appointment-p">
              {{ getActiveSubscriptionCustomerName() }}
            </p>
          </div>
          <div class="form-group">
            <label for="order-status">Vehicle License Plate</label>
            <p class="appointment-p">{{ getOrderSuccessLicensePlateDisplay() }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Package Name</label>
            <p class="appointment-p">{{ notification?.data.package_name }}</p>
          </div>
        </div>
      </div>
      <div
        class=""
        *ngIf="
          notification?.type === 'active-subscription' && !shouldShowVendorActiveSubscriptionModal()
        "
      >
        <div class="col-12-row">
          <div class="form-group">
            <label for="order-id">Customer Name</label>
            <p class="appointment-p">
              {{ getActiveSubscriptionCustomerName() }}
            </p>
          </div>
          <div class="form-group">
            <label for="order-status">Vehicle License Plate</label>
            <p class="appointment-p">{{ getOrderSuccessLicensePlateDisplay() }}</p>
          </div>
          <div class="form-group">
            <label for="vehicle-year">Vehicle Year</label>
            <p class="appointment-p">
              {{ getActiveSubscriptionVehicleAttr('year') }}
            </p>
          </div>
          <div class="form-group">
            <label for="vehicle-make">Vehicle Make</label>
            <p class="appointment-p">
              {{ getActiveSubscriptionVehicleAttr('make') }}
            </p>
          </div>
          <div class="form-group">
            <label for="vehicle-model">Vehicle Model</label>
            <p class="appointment-p">
              {{ getActiveSubscriptionVehicleAttr('model') }}
            </p>
          </div>
          <div class="form-group">
            <label for="order-status">Package Name</label>
            <p class="appointment-p">
              {{ notification?.data.package_name }}
              <!-- CWS-1582 §8: Family plan, and Primary on the primary's membership. -->
              <app-family-tags
                [isFamily]="activeSubscriptionFamilyTags.isFamily"
                [isPrimary]="activeSubscriptionFamilyTags.isPrimary"
              ></app-family-tags>
            </p>
          </div>
          <div class="form-group">
            <label for="order-status">Total Washes Available</label>
            <p class="appointment-p">{{ getActiveSubscriptionWashesAvailable() }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Total Washes Redeemed</label>
            <p class="appointment-p">{{ notification?.data.total_washes_redeemed || '0' }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Last Washed Date</label>
            <p class="appointment-p">{{ getFormattedActiveSubscriptionLastWashedDate() }}</p>
          </div>
          <div class="form-group">
            <label for="order-status">Status</label>
            <p class="appointment-p">{{ getActiveSubscriptionStatusLabel() }}</p>
          </div>
        </div>
      </div>
      <div
        class=""
        *ngIf="
          notification?.type === 'vehicle-not-found' || notification?.type === 'order-not-found'
        "
      >
        <div class="col-12-row">
          <div class="form-group">
            <label for="vehicle-license-plate">Vehicle License Plate</label>
            <div class="license-plate-input-with-loader">
              <input
                type="text"
                id="vehicle-license-plate"
                [(ngModel)]="formData.vehicleLicensePlate"
                [disabled]="isApplyingLprPlateCorrection"
                class="form-control"
                placeholder="Enter vehicle license plate"
              />
              <div
                class="lpr-plate-correction-loader"
                *ngIf="isApplyingLprPlateCorrection"
                aria-hidden="true"
              >
                <div class="lpr-plate-correction-spinner"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Errors + actions stay pinned below the scrollable body so buttons are never clipped -->
    <div class="modal-footer-region">
      <!-- Error Message -->
      <div
        *ngIf="updateOrderError"
        class="error-message"
        [class.error-message--with-action]="shouldShowReactivateCustomerAction()"
      >
        <span>{{ updateOrderError }}</span>
        <button
          *ngIf="shouldShowReactivateCustomerAction()"
          type="button"
          class="error-message__action"
          [disabled]="isReactivatingCustomer"
          (click)="onReactivateCustomer()"
        >
          {{ isReactivatingCustomer ? 'Reactivating…' : 'Reactivate customer' }}
        </button>
      </div>

      <!-- Footer -->
      <app-modal-footer-actions
        footerClass="modal-footer modal-footer--no-top-border"
        [showSecondary]="false"
        [showCancel]="true"
        [showPrimary]="footerShowPrimary"
        [cancelText]="footerCancelText"
        [cancelDisabled]="footerCancelDisabled"
        [primaryText]="footerPrimaryText"
        [primaryDisabled]="footerPrimaryDisabled"
        [primaryLoading]="footerPrimaryLoading"
        [primaryLoadingText]="footerPrimaryLoadingText"
        [primaryExtraClass]="footerPrimaryExtraClass"
        [stackOnMobile]="true"
        (cancelClick)="onFooterCancelClick()"
        (primaryClick)="onFooterPrimaryClick()"
      />
    </div>
    <!-- /.modal-footer-region -->

    <ng-template #orderRequestCardsShimmer>
      <div
        class="order-request-cards-shimmer order-request-static-cards"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading"
      >
        <div
          *ngFor="let _ of orderRequestSectionShimmerSlots"
          class="order-request-cards-shimmer__card"
          aria-hidden="true"
        >
          <div
            class="order-request-cards-shimmer__line order-request-cards-shimmer__line--title"
          ></div>
          <div
            class="order-request-cards-shimmer__line order-request-cards-shimmer__line--sub"
          ></div>
        </div>
      </div>
    </ng-template>
  </div>
</div>

<!-- Way+ benefits (checkout summary → Figma 175-15228). -->
<div
  *ngIf="checkoutWayPlusBenefitsModalOpen"
  class="checkout-wayplus-benefits-overlay"
  (click)="closeCheckoutWayPlusBenefitsModal()"
>
  <div
    class="checkout-wayplus-benefits-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="checkout-wayplus-benefits-title"
    (click)="$event.stopPropagation()"
  >
    <button
      type="button"
      class="checkout-wayplus-benefits-modal__close"
      (click)="closeCheckoutWayPlusBenefitsModal()"
      aria-label="Close benefits"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M18 6L6 18M6 6L18 18"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
    <div class="checkout-wayplus-benefits-modal__hero">
      <span class="checkout-wayplus-benefits-modal__badge"
        >{{ checkoutWayPlusStaticBenefitsCount }} FREE</span
      >
      <h2 id="checkout-wayplus-benefits-title" class="checkout-wayplus-benefits-modal__title">
        Benefits Await You!
      </h2>
      <p class="checkout-wayplus-benefits-modal__subtitle">
        Enjoy all {{ checkoutWayPlusStaticBenefitsCount }} benefits when you complete your 50th
        single car wash order.
      </p>
    </div>
    <div class="checkout-wayplus-benefits-modal__list">
      <div
        *ngFor="
          let item of checkoutWayPlusBenefitModalItems;
          trackBy: trackCheckoutWayPlusBenefitByTitle
        "
        class="checkout-wayplus-benefits-modal__row"
      >
        <div class="checkout-wayplus-benefits-modal__icon-wrap" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.75" />
            <path
              d="M8 12l2.5 2.5L16 9"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="checkout-wayplus-benefits-modal__row-text">
          <div class="checkout-wayplus-benefits-modal__row-title">{{ item.title }}</div>
          <div class="checkout-wayplus-benefits-modal__row-desc">{{ item.description }}</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Product picker overlay (API vending / choose_products add-on). -->
<div *ngIf="orderRequestVendingDialogOpen" class="vending-products-overlay">
  <div
    class="vending-products-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="vending-products-title"
  >
    <div class="vending-products-modal__header">
      <h2 id="vending-products-title" class="vending-products-modal__title">
        {{ getOrderRequestVendingModalHeading() }}
      </h2>
      <button
        type="button"
        class="vending-products-modal__close"
        (click)="closeOrderRequestVendingDialog()"
        [attr.aria-label]="'Close ' + getOrderRequestVendingModalHeading()"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <div class="vending-products-modal__list">
      <p *ngIf="!orderRequestVendingItems.length" class="vending-products-modal__empty">
        No products are configured for this package.
      </p>
      <div
        class="vending-products-row"
        *ngFor="let item of orderRequestVendingItems; trackBy: trackOrderRequestVendingItemById"
        [class.vending-products-row--active]="orderRequestVendingDialogChecked[item.id]"
        [class.vending-products-row--custom-editor]="
          item.allowsCustomPrice && isOrderRequestVendingDialogCustomExpanded(item.id)
        "
        (click)="$event.stopPropagation()"
      >
        <div class="vending-products-row__name">{{ item.name }}</div>

        <div class="vending-products-row__middle" *ngIf="item.allowsCustomPrice">
          <button
            *ngIf="!isOrderRequestVendingDialogCustomExpanded(item.id)"
            type="button"
            class="order-request-addon-pill order-request-addon-pill--outline"
            (click)="openOrderRequestVendingCustomEditor(item, $event)"
          >
            Custom price
          </button>
          <div
            *ngIf="isOrderRequestVendingDialogCustomExpanded(item.id)"
            class="picker-inline-amount-editor vending-products-row__editor"
            (click)="$event.stopPropagation()"
          >
            <label class="picker-inline-amount-editor__label" [for]="'vend-amt-' + item.id"
              >Enter amount</label
            >
            <div class="picker-inline-amount-editor__field">
              <span class="picker-inline-amount-editor__currency" aria-hidden="true">$</span>
              <input
                type="text"
                class="picker-inline-amount-editor__input"
                [id]="'vend-amt-' + item.id"
                [ngModel]="getOrderRequestAddonCustomPriceDraft(item.id)"
                (ngModelChange)="onOrderRequestAddonCustomPriceInput(item.id, $event)"
                inputmode="decimal"
                autocomplete="off"
                placeholder="0.00"
              />
              <button
                type="button"
                class="picker-inline-amount-editor__save"
                (click)="saveOrderRequestVendingItemCustomPrice(item)"
              >
                Save
              </button>
            </div>
          </div>
        </div>

        <div
          *ngIf="!item.allowsCustomPrice && item.quantitySelection === 'multiple'"
          class="vending-products-row__stepper"
        >
          <button
            type="button"
            class="vending-products-row__step"
            (click)="adjustOrderRequestVendingQuantity(item.id, 1)"
            [attr.aria-label]="'Increase quantity for ' + item.name"
          >
            +
          </button>
          <span class="vending-products-row__qty">{{ item.quantity }}</span>
          <button
            type="button"
            class="vending-products-row__step"
            (click)="adjustOrderRequestVendingQuantity(item.id, -1)"
            [disabled]="item.quantity <= 0"
            [attr.aria-label]="'Decrease quantity for ' + item.name"
          >
            -
          </button>
        </div>
        <div
          *ngIf="!item.allowsCustomPrice && item.quantitySelection === 'single'"
          class="vending-products-row__stepper-spacer"
        ></div>

        <div
          class="vending-products-row__price"
          *ngIf="!item.allowsCustomPrice || !isOrderRequestVendingDialogCustomExpanded(item.id)"
        >
          {{ getOrderRequestVendingItemTotal(item) }}
        </div>

        <label class="vending-products-row__check-wrap">
          <input
            type="checkbox"
            class="vending-products-row__check-input"
            [checked]="orderRequestVendingDialogChecked[item.id]"
            (change)="onOrderRequestVendingDialogCheckboxChange(item, $event)"
            [attr.aria-label]="'Select ' + item.name"
          />
        </label>
      </div>
    </div>

    <div class="vending-products-modal__footer">
      <p class="vending-products-modal__hint">
        Check the items to include. Use + / − when quantity is multiple. Custom-price rows need a
        saved amount. Uncheck all to remove this add-on.
      </p>
      <button
        type="button"
        class="vending-products-modal__continue"
        (click)="continueOrderRequestVendingSelection()"
      >
        Continue
      </button>
    </div>
  </div>
</div>

<!-- Add-on variants (multi-variant package). -->
<div
  *ngIf="orderRequestAddonVariantsDialog as dlg"
  class="addon-variants-overlay"
  (click)="closeOrderRequestAddonVariantsDialog()"
>
  <div
    class="addon-variants-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="addon-variants-title"
    (click)="$event.stopPropagation()"
  >
    <div class="addon-variants-modal__header">
      <h2 id="addon-variants-title" class="addon-variants-modal__title">{{ dlg.label }}</h2>
      <button
        type="button"
        class="addon-variants-modal__close"
        (click)="closeOrderRequestAddonVariantsDialog()"
        aria-label="Close variants"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
    <div class="addon-variants-modal__list addon-variants-modal__list--blocks">
      <div
        class="addon-variants-modal__block"
        *ngFor="let v of dlg.packageVariants; trackBy: trackOrderRequestAddonVariantById"
        [class.addon-variants-modal__block--checked]="isOrderRequestAddonVariantDialogChecked(v.id)"
        (click)="$event.stopPropagation()"
      >
        <div
          class="vending-products-row vending-products-row--addon-variants"
          [class.vending-products-row--custom-editor]="
            v.mode === 'custom_price' &&
            !formatOrderRequestAddonSavedPrice(v.id) &&
            isOrderRequestAddonVariantDialogCustomExpanded(v.id)
          "
        >
          <div class="vending-products-row__name">{{ v.label }}</div>

          <div
            class="vending-products-row__middle"
            *ngIf="
              v.mode === 'custom_price' &&
              !formatOrderRequestAddonSavedPrice(v.id) &&
              !isOrderRequestAddonVariantDialogCustomExpanded(v.id)
            "
          >
            <button
              type="button"
              class="addon-variants-modal__row-tag addon-variants-modal__row-tag--outline"
              (click)="openOrderRequestAddonVariantDialogCustomEditor(v, $event)"
            >
              Custom price
            </button>
          </div>
          <div
            class="vending-products-row__middle"
            *ngIf="
              v.mode === 'custom_price' &&
              !formatOrderRequestAddonSavedPrice(v.id) &&
              isOrderRequestAddonVariantDialogCustomExpanded(v.id)
            "
          >
            <div
              class="picker-inline-amount-editor vending-products-row__editor"
              (click)="$event.stopPropagation()"
            >
              <label class="picker-inline-amount-editor__label" [for]="'var-amt-' + v.id"
                >Enter amount</label
              >
              <div class="picker-inline-amount-editor__field">
                <span class="picker-inline-amount-editor__currency" aria-hidden="true">$</span>
                <input
                  type="text"
                  class="picker-inline-amount-editor__input"
                  [id]="'var-amt-' + v.id"
                  [ngModel]="getOrderRequestAddonCustomPriceDraft(v.id)"
                  (ngModelChange)="onOrderRequestAddonCustomPriceInput(v.id, $event)"
                  inputmode="decimal"
                  autocomplete="off"
                  placeholder="0.00"
                />
                <button
                  type="button"
                  class="picker-inline-amount-editor__save"
                  (click)="saveOrderRequestAddonVariantLineCustomPrice(v)"
                >
                  Save
                </button>
              </div>
            </div>
          </div>

          <div *ngIf="showAddonVariantStepperInDialog(v)" class="vending-products-row__stepper">
            <button
              type="button"
              class="vending-products-row__step"
              (click)="adjustOrderRequestAddonVariantsQty(v.id, 1)"
              [attr.aria-label]="'Increase quantity for ' + v.label"
            >
              +
            </button>
            <span class="vending-products-row__qty">{{
              getOrderRequestAddonVariantsDialogQty(v.id)
            }}</span>
            <button
              type="button"
              class="vending-products-row__step"
              (click)="adjustOrderRequestAddonVariantsQty(v.id, -1)"
              [disabled]="getOrderRequestAddonVariantsDialogQty(v.id) <= 0"
              [attr.aria-label]="'Decrease quantity for ' + v.label"
            >
              -
            </button>
          </div>
          <div
            *ngIf="!showAddonVariantStepperInDialog(v)"
            class="vending-products-row__stepper-spacer"
          ></div>

          <div class="addon-variants-modal__row-tail">
            <div
              class="vending-products-row__price addon-variants-modal__row-price"
              *ngIf="
                v.mode !== 'custom_price' || !isOrderRequestAddonVariantDialogCustomExpanded(v.id)
              "
            >
              {{ getOrderRequestAddonVariantDialogPriceDisplay(v) }}
            </div>
            <label
              class="vending-products-row__check-wrap addon-variants-modal__select-wrap"
              *ngIf="isOrderRequestAddonVariantDialogCheckboxVisible(v)"
            >
              <input
                type="checkbox"
                class="addon-variants-modal__radio-select"
                [checked]="isOrderRequestAddonVariantDialogChecked(v.id)"
                (change)="onOrderRequestAddonVariantDialogCheckboxChange(v, $event)"
                [attr.aria-label]="'Select ' + v.label"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="addon-variants-modal__footer">
      <button
        type="button"
        class="vending-products-modal__continue"
        (click)="applyOrderRequestAddonVariantsDialog()"
      >
        Continue
      </button>
    </div>
  </div>
</div>

<app-rewash-reason-modal
  *ngIf="isOpen"
  [isOpen]="orderRequestRewashReasonModalOpen"
  [originalOrderId]="orderRequestRewashTarget?.originalOrderId ?? null"
  [originalSubscriptionWashId]="orderRequestRewashTarget?.originalSubscriptionWashId ?? null"
  [originalPrepaidRedemptionId]="orderRequestRewashTarget?.originalPrepaidRedemptionId ?? null"
  [laneId]="rewashLaneId"
  (closed)="closeOrderRequestRewashReasonModal()"
  (confirmed)="onOrderRequestRewashConfirmed($event)"
></app-rewash-reason-modal>

<!-- Full vehicle image preview (opened from the order-request plate thumbnail). -->
<div
  *ngIf="orderRequestVehicleImagePreviewOpen && orderRequestLprVehicleImageUrl as vehicleImageUrl"
  class="vehicle-image-preview-overlay"
  (click)="closeOrderRequestVehicleImagePreview()"
>
  <div
    class="vehicle-image-preview-modal"
    role="dialog"
    aria-modal="true"
    aria-label="Full vehicle image"
    (click)="$event.stopPropagation()"
  >
    <div class="vehicle-image-preview-modal__header">
      <span class="vehicle-image-preview-modal__title">{{
        orderRequestVehicleImagePreviewTitle
      }}</span>
      <button
        type="button"
        class="vehicle-image-preview-modal__close"
        (click)="closeOrderRequestVehicleImagePreview()"
        aria-label="Close image preview"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 5L5 15M5 5L15 15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <img
      class="vehicle-image-preview-modal__image"
      [src]="vehicleImageUrl"
      alt="Full vehicle capture from lane camera"
      referrerpolicy="no-referrer"
    />
  </div>
</div>
<!-- Payment success (all methods except payment-link waiting UI) -->
<app-success-modal
  [isVisible]="showPaymentSuccess"
  [data]="successModalData"
  [isOpeningGate]="isOpeningGate"
  [plates]="familySaleResult?.plates ?? null"
  (close)="onPaymentSuccessModalClose()"
  (action)="onPaymentSuccessAction()"
  (printInvoice)="onPrintInvoice()"
  (openGate)="onOpenGate()"
></app-success-modal>

<!-- Prepaid wash redemption success (no payment checkout) -->
<app-prepaid-redeemed-success-modal
  [isVisible]="showPrepaidRedeemedSuccess"
  [data]="prepaidRedeemedSuccessData"
  [title]="prepaidRedeemedResult.title"
  [note]="prepaidRedeemedResult.message"
  (close)="onPrepaidRedeemedSuccessModalClose()"
  (primaryAction)="onPrepaidRedeemedSuccessModalAction()"
></app-prepaid-redeemed-success-modal>

<!-- Vendor membership barcode scanner (Scan by Camera) -->
<app-vendor-membership-barcode-scanner-modal
  [isVisible]="showVendorMembershipBarcodeScanner"
  [isProcessing]="isVendorMembershipLookupLoading"
  [uploadOnly]="vendorMembershipScannerUploadOnly"
  [cameraStreamPromise]="vendorMembershipCameraStreamPromise"
  (close)="onVendorMembershipBarcodeScannerClose()"
  (scanned)="onVendorMembershipBarcodeScanned($event)"
></app-vendor-membership-barcode-scanner-modal>

<!--
  CWS-1908 parity: the redemption-code fallback the navbar scan has always had.
  A QR that cannot be read, or that the lookup refuses, falls through to here so the
  attendant can type the code from the customer's SMS instead of hitting a dead end.
-->
<app-prepaid-code-dialog
  [isOpen]="isPrepaidCodeDialogOpen"
  [busy]="prepaidCodeBusy"
  [error]="prepaidCodeError"
  [intro]="prepaidCodeIntro"
  (find)="onPrepaidCodeFind($event)"
  (closed)="closePrepaidCodeDialog()"
></app-prepaid-code-dialog>

<!-- CWS-1914: prepaid redemption QR (Redeem Prepaid), same scanner as the membership one -->
<app-vendor-membership-barcode-scanner-modal
  [isVisible]="showPrepaidRedemptionQrScanner"
  [isProcessing]="isPrepaidRedemptionQrProcessing"
  [scanSubject]="'prepaid'"
  [cameraStreamPromise]="prepaidRedemptionQrCameraStreamPromise"
  (close)="onPrepaidRedemptionQrScannerClose()"
  (scanned)="onPrepaidRedemptionQrScanned($event)"
  (qrUnreadable)="onPrepaidRedemptionQrUnreadable()"
></app-vendor-membership-barcode-scanner-modal>

<!-- Vendor membership device scan (Verify the membership source → Using the shop's device) -->
<app-vendor-membership-device-scan-modal
  [isVisible]="orderRequestMembershipShopDeviceWaiting"
  [isProcessing]="isVendorMembershipDeviceLookupLoading"
  (close)="onVendorMembershipDeviceScanClose()"
  (scanned)="onVendorMembershipDeviceScanned($event)"
></app-vendor-membership-device-scan-modal>

<!-- Vendor (DRB) active subscription — Figma layout when SiteWatch sync is live -->
<app-vendor-active-subscription-modal
  [isVisible]="shouldShowVendorActiveSubscriptionModal()"
  [data]="getVendorActiveSubscriptionDisplay()"
  [primaryButtonLabel]="getVendorActiveSubscriptionPrimaryButtonLabel()"
  [isPrimaryActionDisabled]="isOpeningGate || isRedeemingReactivated"
  [isConfirmPaymentDisabled]="isConfirmingCashPayment || isProcessingPayment"
  [isMobileOrTablet]="isMobileOrTablet"
  [showTerminalPaymentMethod]="shouldShowTerminalPaymentMethod()"
  [isTerminalPaymentMethodEnabled]="isTerminalPaymentMethodEnabled()"
  [errorMessage]="updateOrderError"
  [isCardFormComplete]="isVendorActiveCardFormComplete()"
  [cardholderName]="paymentForm.cardholderName"
  [billingAddress]="paymentForm.billingAddress"
  [showUpgradePlanPicker]="showVendorUpgradePlanPicker"
  [showPlanChange]="showVendorPlanChange"
  [canChangePlan]="canChangeVendorPlan()"
  [canManagePause]="canManageVendorPause()"
  [canCancelMembership]="canCancelVendorMembership()"
  [canStartFamily]="canStartVendorFamily()"
  [lprEntryId]="vendorLprEntryId()"
  [planChangeLocationId]="resolveVendorPlanChangeLocationId()"
  [planChangeNotice]="vendorPlanChangeNotice"
  [planChangeUpcoming]="vendorPlanChangeUpcoming"
  [upgradePlans]="vendorUpgradePlans"
  [upgradeDiscounts]="vendorUpgradeDiscounts"
  [isLoadingUpgradePlans]="isLoadingVendorUpgradePlans"
  [isLoadingHistory]="isLoadingOrderRequestHistoryPage"
  [historyBanner]="orderRequestTodayBanner"
  [historyExpanded]="orderRequestHistoryExpanded"
  [rewashEligibility]="orderRequestRewashEligibility"
  [membershipRewashEligibility]="orderRequestMembershipRewashEligibility"
  [prepaidRewashEligibility]="orderRequestPrepaidRewashEligibility"
  (close)="onVendorActiveSubscriptionModalClose()"
  (primaryAction)="onVendorActiveSubscriptionModalPrimary()"
  (upgradePlan)="onVendorActiveSubscriptionUpgradePlan()"
  (planChange)="onVendorActiveSubscriptionPlanChange()"
  (planChangeBack)="onVendorPlanChangeBack()"
  (planChangeApplied)="onVendorPlanChangeApplied($event)"
  (pauseChanged)="onVendorPauseChanged($event)"
  (membershipCancelled)="onVendorMembershipCancelled($event)"
  (familyStarted)="onVendorFamilyStarted($event)"
  (upgradePlanBack)="onVendorUpgradePlanBack()"
  (upgradePlanConfirm)="onVendorUpgradePlanConfirm($event)"
  (confirmPayment)="onVendorActiveSubscriptionConfirmPayment($event)"
  (paymentMethodChange)="onVendorActivePaymentMethodChange($event)"
  (cardholderNameChange)="paymentForm.cardholderName = $event"
  (billingAddressChange)="paymentForm.billingAddress = $event"
  (historyToggle)="toggleOrderRequestHistory()"
  (historyPageChange)="goToOrderRequestHistoryPage($event)"
  (historyPageSizeChange)="orderRequestHistoryPageSizeChange($event)"
  (rewashClick)="openOrderRequestRewashReasonModal($event)"
></app-vendor-active-subscription-modal>

<!-- CWS-1038: z-index 12000 — above barcode (11200) and device-scan (11300) overlays -->
<app-confirmation-modal
  [isVisible]="showVendorMembershipVehicleLinkConfirm"
  [data]="vendorMembershipVehicleLinkConfirmData"
  (cancel)="onVendorMembershipVehicleLinkConfirmCancel()"
  (confirm)="onVendorMembershipVehicleLinkConfirmAccept()"
></app-confirmation-modal>
